import { type JSONData } from "@/data/base_classes/dto.d";
import { FormValues } from "./types.d";
import { Media } from "@/data/entities";

/**
 * The form values are an object, where the key are dot-separated paths to their actual location in the corresponding JSONData object
 * @param formValues The form values to convert to JSON data.
 */
export function formValuesToJSONData(formValues: FormValues): JSONData {
  if (typeof formValues !== "object") {
    return formValues;
  }

  const jsonData: JSONData = {};

  for (const key in formValues) {
    if (formValues.hasOwnProperty(key)) {
      const value = formValues[key] as any;

      if (Array.isArray(value)) {
        if (value.every((item) => typeof item === "object")) {
          setValue(
            key,
            value.map((item) => formValuesToJSONData(item as FormValues)),
            jsonData,
          );
        } else {
          setValue(key, value, jsonData);
        }
      } else if (typeof value === "object") {
        Object.keys(value).forEach((nestedKey) => {
          setValue(`${key}.${nestedKey}`, value[nestedKey], jsonData);
        });
      } else {
        setValue(key, value, jsonData);
      }
    }
  }

  return jsonData;
}

/**
 * Set the value at the given path in the given object.
 * @param path The dot-separated path to the location where the value should be set.
 * @param value The value to be set.
 * @param obj The object in which the value should be set.
 */
function setValue(path: string, value: any, obj: JSONData) {
  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (i === keys.length - 1) {
      // Last key, set the value
      current[key] = value;
    } else {
      // Intermediate keys, ensure the object exists
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key] as JSONData;
    }
  }
}

/**
 * The JSON data is an object, which we need to traverse and create form values from.
 * @param jsonData The JSON data to convert to form values.
 */
export function JSONDataToFormValues(jsonData: JSONData): FormValues {
  const formValues: FormValues = {};

  traverseJSONData("", jsonData, formValues);

  return formValues;
}

function traverseJSONData(path: string, value: any, formValues: FormValues) {
  if (typeof value === "object" && !Array.isArray(value)) {
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        const newPath = path ? `${path}.${key}` : key;
        traverseJSONData(newPath, value[key], formValues);
      }
    }
  } else if (Array.isArray(value)) {
    if (
      value.every((item) => typeof item === "object" && !Array.isArray(item))
    ) {
      formValues[path] = value.map((item) => {
        const nestedFormValues: FormValues = {};
        traverseJSONData("", item, nestedFormValues);
        return nestedFormValues;
      });
    } else {
      formValues[path] = value;
    }
  } else {
    formValues[path] = value;
  }
}
